/**
 * Annahme-/Übergabeprotokoll-Modal (Jun-2026, User-Wunsch).
 *
 * - „Leeres Protokoll drucken/ansehen": öffnet das PDF mit leerem Unterschriftsfeld.
 * - Tablet-Unterschrift per Canvas → wird beim Speichern ins PDF eingebettet.
 * - Das gespeicherte PDF wird an den Auftrag gehängt (und im Kundenportal sichtbar).
 */
import { useRef, useState, useEffect } from "react";
import { X, Printer, FileSignature, Loader2, Eraser, Save } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { previewPdfRequest } from "../../lib/pdfPreview";

export default function HandoverProtocolModal({ orderId, orderNumber, onClose, onSaved }) {
    const canvasRef = useRef(null);
    const drawing = useRef(false);
    const hasInk = useRef(false);
    const [leihwagen, setLeihwagen] = useState("");
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        const c = canvasRef.current;
        if (!c) return;
        const ctx = c.getContext("2d");
        ctx.lineWidth = 2.5;
        ctx.lineCap = "round";
        ctx.strokeStyle = "#111827";
    }, []);

    const pos = (e) => {
        const c = canvasRef.current;
        const r = c.getBoundingClientRect();
        const t = e.touches ? e.touches[0] : e;
        return { x: (t.clientX - r.left) * (c.width / r.width), y: (t.clientY - r.top) * (c.height / r.height) };
    };
    const start = (e) => { e.preventDefault(); drawing.current = true; const ctx = canvasRef.current.getContext("2d"); const p = pos(e); ctx.beginPath(); ctx.moveTo(p.x, p.y); };
    const move = (e) => { if (!drawing.current) return; e.preventDefault(); const ctx = canvasRef.current.getContext("2d"); const p = pos(e); ctx.lineTo(p.x, p.y); ctx.stroke(); hasInk.current = true; };
    const end = () => { drawing.current = false; };
    const clear = () => { const c = canvasRef.current; c.getContext("2d").clearRect(0, 0, c.width, c.height); hasInk.current = false; };

    const openPreview = () => {
        previewPdfRequest(
            api.get(`/work-orders/${orderId}/handover-protocol.pdf`, {
                params: leihwagen ? { leihwagen } : {},
                responseType: "blob",
            }),
            `Annahmeprotokoll_${orderNumber || orderId}.pdf`,
        );
    };

    const save = async () => {
        setSaving(true);
        try {
            const signature = hasInk.current ? canvasRef.current.toDataURL("image/png") : null;
            const { data } = await api.post(`/work-orders/${orderId}/handover-protocol`, { signature, leihwagen: leihwagen || undefined });
            toast.success(signature ? "Protokoll mit Unterschrift gespeichert & an Auftrag angehängt" : "Protokoll gespeichert & an Auftrag angehängt");
            previewPdfRequest(
                api.get(String(data.document.url).replace(/^\/api/, ""), { responseType: "blob" }),
                `Annahmeprotokoll_${orderNumber || orderId}.pdf`,
            );
            onSaved?.(data.document);
            onClose();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Speichern fehlgeschlagen");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="fixed inset-0 z-[60] bg-black/80 flex items-center justify-center p-4" data-testid="handover-protocol-modal">
            <div className="bg-white rounded-sm w-full max-w-lg max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center justify-between p-4 border-b border-gray-200 sticky top-0 bg-white">
                    <h2 className="font-display font-black text-lg flex items-center gap-2">
                        <FileSignature className="w-5 h-5 text-[#FFCC00]" /> Übergabeprotokoll
                        <span className="text-xs font-mono-num text-gray-400">{orderNumber}</span>
                    </h2>
                    <button onClick={onClose} className="p-1 rounded-sm hover:bg-gray-100" data-testid="handover-close"><X className="w-5 h-5" /></button>
                </div>

                <div className="p-5 space-y-4">
                    <p className="text-sm text-gray-600">
                        Erzeugt ein Annahmeprotokoll mit Fahrzeug-/Kundendaten, Mängelbeschreibung,
                        Schadensfotos (vom Handy-Scan), Kostenvoranschlag-Hinweis &amp; DSGVO-Text.
                    </p>

                    <div>
                        <label className="text-xs font-bold uppercase tracking-wider text-gray-700">Leihfahrzeug (optional)</label>
                        <Textarea value={leihwagen} onChange={(e) => setLeihwagen(e.target.value)} rows={2}
                            placeholder="z. B. BMW G310R · Kennzeichen B-LW 100 · vollgetankt übergeben"
                            className="rounded-sm mt-1" data-testid="handover-leihwagen" />
                    </div>

                    <div>
                        <div className="flex items-center justify-between mb-1">
                            <label className="text-xs font-bold uppercase tracking-wider text-gray-700">Unterschrift Kunde (am Tablet)</label>
                            <button type="button" onClick={clear} className="text-[11px] font-bold text-gray-500 hover:text-red-600 inline-flex items-center gap-1" data-testid="handover-sig-clear">
                                <Eraser className="w-3.5 h-3.5" /> Löschen
                            </button>
                        </div>
                        <canvas
                            ref={canvasRef}
                            width={520}
                            height={180}
                            className="w-full h-40 border-2 border-dashed border-gray-300 rounded-sm bg-gray-50 touch-none cursor-crosshair"
                            onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
                            onTouchStart={start} onTouchMove={move} onTouchEnd={end}
                            data-testid="handover-sig-canvas"
                        />
                        <p className="text-[11px] text-gray-400 mt-1">Mit Finger/Stift unterschreiben — oder ohne Unterschrift speichern/drucken und manuell unterschreiben.</p>
                    </div>

                    <div className="flex flex-wrap gap-2 pt-1">
                        <Button type="button" variant="outline" onClick={openPreview} className="border-2 rounded-sm" data-testid="handover-print">
                            <Printer className="w-4 h-4 mr-1.5" /> Leeres Protokoll drucken
                        </Button>
                        <Button type="button" onClick={save} disabled={saving} className="bg-[#FFCC00] hover:bg-[#E6B800] text-[#111827] font-bold rounded-sm ml-auto" data-testid="handover-save">
                            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Save className="w-4 h-4 mr-1.5" />} Speichern &amp; anhängen
                        </Button>
                    </div>
                </div>
            </div>
        </div>
    );
}
